import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  LINK_FEED_LOTTERY_QUEUE_DEFAULT,
  type LinkFeedQueueKey,
  type LinkIp4ConnectorSource,
  type LinkIp4Queue,
} from '@reform-lite/contracts';
import { apiClient } from '@/lib/api-client';

/**
 * VL-353 — integration profile 4 ("Document feed (message queue)") sources.
 * Back Office stores them and hands them to the Link of their store, which
 * publishes the store's till documents to a message broker; Back Office never
 * connects to the broker itself. The password is write-only: no response
 * carries it, so the form can only replace it.
 */

export type Ip4Protocol = LinkIp4ConnectorSource['connection']['protocol'];
export type Ip4Queue = LinkIp4Queue;
export type Ip4Feed = LinkIp4ConnectorSource['feed'];

/** Where the broker is. Never the password. */
export type Ip4Connection = {
  protocol: Ip4Protocol;
  host: string;
  port: number;
  vhost: string;
  /** Not edited on the screen; the server defaults them and an edit keeps them. */
  heartbeatSeconds?: number;
  connectTimeoutMs?: number;
  credentials: { username: string };
};

export type Ip4Source = {
  id: string;
  profile: 'ip-4';
  name: string;
  /** The master switch. Off pauses every queue; nothing is skipped. */
  enabled: boolean;
  updatedAt: string;
  connection: Ip4Connection;
  /** Null only for a stored source Back Office can no longer read as a feed. */
  feed: Ip4Feed | null;
  location: { id: string; code: string; name: string };
};

export type Ip4SourceInput = {
  locationId: string;
  name: string;
  enabled: boolean;
  connection: Omit<Ip4Connection, 'credentials'> & {
    credentials: { username: string; password: string };
  };
  feed: Ip4Feed;
};

/**
 * An edit. A section is sent whole when anything in it changed, and left out
 * otherwise (the server merges what it is sent over what is stored);
 * `credentials.password` absent keeps the stored one.
 */
export type Ip4SourceChange = {
  name?: string;
  enabled?: boolean;
  connection?: Omit<Ip4Connection, 'credentials'> & {
    credentials: { username: string; password?: string };
  };
  feed?: Ip4Feed;
  /** The `updatedAt` the form loaded; the server refuses a stale edit (409). */
  expectedUpdatedAt?: string;
};

/**
 * The routing each queue starts with — the one place the defaults live. They
 * are only what the form is pre-filled with; the operator can change each.
 */
export const IP4_DEFAULT_QUEUES: Record<
  LinkFeedQueueKey,
  { exchange: string; routingKey: string }
> = {
  sales: { exchange: 'all_checks_exchange', routingKey: 'all_checks' },
  cancellations: { exchange: 'cancel_checks_exchange', routingKey: 'cancel_checks' },
  returns: { exchange: 'return_checks_exchange', routingKey: 'return_checks' },
  cashMovements: { exchange: 'in_out_checks_exchange', routingKey: 'in_out_checks' },
  zReports: { exchange: 'z_reports_exchange', routingKey: 'z_reports' },
  // VL-375: not given by the store yet; a new source starts with it off.
  lotterySales: {
    exchange: LINK_FEED_LOTTERY_QUEUE_DEFAULT.exchange,
    routingKey: LINK_FEED_LOTTERY_QUEUE_DEFAULT.routingKey,
  },
};

/** Whether a new source starts with the queue on: every one but the State Lottery queue. */
export const IP4_QUEUE_STARTS_ON: Record<LinkFeedQueueKey, boolean> = {
  sales: true,
  cancellations: true,
  returns: true,
  cashMovements: true,
  zReports: true,
  lotterySales: LINK_FEED_LOTTERY_QUEUE_DEFAULT.enabled,
};

/** The registered port of each protocol, offered until the operator types another. */
export const IP4_DEFAULT_PORT: Record<Ip4Protocol, number> = { amqp: 5672, amqps: 5671 };

/** Shorter passwords are refused: they could not be masked by value in logs. */
export const IP4_PASSWORD_MIN_LENGTH = 4;

export const IP4_HEADER_LINES_MAX = 10;
export const IP4_FOOTER_LINES_MAX = 4;
export const IP4_LINE_MAX_LENGTH = 96;

/** What each queue carries, in the operator's words. */
export const IP4_QUEUE_LABEL_KEY: Record<LinkFeedQueueKey, string> = {
  sales: 'settings.integrations.ip4.queue.sales',
  cancellations: 'settings.integrations.ip4.queue.cancellations',
  returns: 'settings.integrations.ip4.queue.returns',
  cashMovements: 'settings.integrations.ip4.queue.cashMovements',
  zReports: 'settings.integrations.ip4.queue.zReports',
  lotterySales: 'settings.integrations.ip4.queue.lotterySales',
};

/**
 * A source as the form reads it. One stored before VL-375 has no
 * `lotterySales` queue; it is shown as what the Link runs it as — off, with
 * the default routing — so the form never reads a queue that is not there.
 */
export function withIp4QueueDefaults(source: Ip4Source): Ip4Source {
  const feed = source.feed;
  if (feed === null || feed.queues.lotterySales !== undefined) return source;
  return {
    ...source,
    feed: {
      ...feed,
      queues: { ...feed.queues, lotterySales: { ...LINK_FEED_LOTTERY_QUEUE_DEFAULT } },
    },
  };
}

const KEY = ['ip4-sources'] as const;
const PATH = '/connectors/ip-4/sources';

/**
 * A finished mutation keeps what it was called with — here, the typed
 * password — in the query client for five minutes by default. Zero drops it
 * as soon as the form that made the call is closed.
 */
const FORGET_ON_CLOSE = 0;

export function useIp4Sources(enabled: boolean) {
  return useQuery({
    queryKey: KEY,
    enabled,
    queryFn: async (): Promise<Ip4Source[]> => {
      const response = await apiClient.get<Ip4Source[]>(PATH);
      return response.data.map(withIp4QueueDefaults);
    },
  });
}

export function useCreateIp4Source() {
  const queryClient = useQueryClient();
  return useMutation({
    gcTime: FORGET_ON_CLOSE,
    mutationFn: async (input: Ip4SourceInput) => {
      await apiClient.post(PATH, input);
    },
    // After a refusal too: "this store already has a feed" means the list on
    // screen is the stale one.
    onSettled: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateIp4Source() {
  const queryClient = useQueryClient();
  return useMutation({
    gcTime: FORGET_ON_CLOSE,
    mutationFn: async (input: { sourceId: string; change: Ip4SourceChange }) => {
      await apiClient.patch(`${PATH}/${input.sourceId}`, input.change);
    },
    // After a refusal too. A stale edit (409) is refused because the list on
    // screen is old; without a fresh one, opening the source again would load
    // the same `updatedAt` and be refused again, until the page is reloaded.
    onSettled: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}
